<template>
   <div class="upload-wrapper">
      <div v-if="urls || readonly" class="img-wrap">
         <img v-if="urls && fileType == '1'" :src="urls" alt="图片" />
         <div v-if="urls && fileType == '2'" class="file-name">{{ fileName }}</div>
         <video v-if="urls && fileType == '3'" :src="urls"></video>
         <div v-if="!urls && fileType == '1'">暂无图片数据</div>
         <el-button type="text" class="img-close" @click="onClose" v-if="!readonly">
            <i class="el-icon-circle-cross"></i>
         </el-button>
      </div>
      <el-upload v-else drag :action="actionUrl" :headers="uploadHeaders" :with-credentials="true" name="uploadFile"
         type="drag" :thumbnail-mode="true" :show-file-list="false" :accept="accept"
         :before-upload="handleBeforeUpload" :on-success="onSuccess" :on-error="onError" :on-remove="onRemove">
         <div>
            <i class="el-icon-upload"></i>
            <div class="el-dragger__text">
               将文件拖到此处，或<em>点击上传</em>
            </div>
         </div>
         <div class="el-upload__tip upload-tips" slot="tip">{{ tips }}</div>
      </el-upload>
   </div>
</template>

<script>
/** 与运营后台上传组件日志筛选一致 */
const LOG_TAG_UPLOAD_ITEM = "[UploadItem]";
export default {
   props: {
      uploadImageType: {
         type: String,
         default: "1",
      },
      fileType: {
         type: String,
         default: "1",
      },
      accept: {
         type: String,
         default: "image/*",
      },
      keys: {
         type: String,
         default: "keys",
      },
      urls: {
         type: String,
         default: "",
      },
      readonly: {
         type: Boolean,
         default: false,
      },
      tips: {
         type: String,
         default: "",
      },
      maxVideoDurationSec: {
         type: Number,
         default: 0
      },
      maxVideoSizeMb: {
         type: Number,
         default: 0
      },
      videoLimitToast: {
         type: String,
         default: ""
      }
   },

   data() {
      return {
         uploadUrl: "",
         fileName: "",
      };
   },

   computed: {
      actionUrl() {
         const url = this.$util.domain + '/adminapi/v1/basicTools/v650/file/upload';
         const param = `?uploadImageType=${this.uploadImageType}&fileType=${this.fileType}`;
         return url + param;
      },
      uploadHeaders() {
         return {
            Authorization: 'Bearer ' + (localStorage.getItem('token') || '')
         };
      }
   },

   watch: {
      urls: {
         handler(value) {
            this.uploadUrl = value;
         },
         immediate: true,
      },
   },

   methods: {
      // 删除图片
      onClose() {
         this.uploadUrl = "";
         this.$emit("upload-state", false);
         this.$emit("onUrls", this.uploadUrl, this.keys);
      },
      handleBeforeUpload(file) {
         this.$emit("upload-state", true);
         if (String(this.fileType) !== "3") {
            return true;
         }
         const toastMsg =
            (this.videoLimitToast && this.videoLimitToast.trim()) ||
            "视频文件不符合限制";
         const maxSizeBytes =
            Number(this.maxVideoSizeMb) > 0 ? Number(this.maxVideoSizeMb) * 1024 * 1024 : 0;
         if (maxSizeBytes > 0 && file && Number(file.size) > maxSizeBytes) {
            this.$emit("upload-state", false);
            this.$util.errorMsg(toastMsg);
            return false;
         }
         const maxDuration = Number(this.maxVideoDurationSec) || 0;
         if (maxDuration <= 0 || !file || !(file instanceof Blob)) {
            return true;
         }
         return this.probeDurationFromBlobFile(file)
            .then((durationMeta) => {
               const rawSeconds = Number(durationMeta && durationMeta.rawSeconds);
               if (durationMeta && durationMeta.ok === true && Number.isFinite(rawSeconds) && rawSeconds > maxDuration) {
                  this.$emit("upload-state", false);
                  this.$util.errorMsg(toastMsg);
                  return Promise.reject(new Error("video duration exceeds limit"));
               }
               return true;
            })
            .catch((err) => {
               if (err && err.message === "video duration exceeds limit") {
                  return Promise.reject(err);
               }
               return true;
            });
      },
      /**
       * 用本地 File 读时长（blob: URL），不受视频 CDN 跨域限制；远程 URL 探测常因无 CORS 失败
       */
      probeDurationFromBlobFile(file) {
         return new Promise((resolve) => {
            if (!file || !(file instanceof Blob)) {
               resolve({ ok: false, seconds: 0 });
               return;
            }
            const objectUrl = URL.createObjectURL(file);
            const video = document.createElement("video");
            video.preload = "metadata";
            video.muted = true;
            video.playsInline = true;
            let settled = false;
            const finish = (result) => {
               if (settled) {
                  return;
               }
               settled = true;
               clearTimeout(timer);
               URL.revokeObjectURL(objectUrl);
               video.removeAttribute("src");
               video.load();
               resolve(result);
            };
            const timer = setTimeout(() => {
               console.warn(LOG_TAG_UPLOAD_ITEM, "probeDurationFromBlobFile timeout");
               finish({ ok: false, seconds: 0 });
            }, 25000);
            video.onloadedmetadata = () => {
               clearTimeout(timer);
               const d = video.duration;
               if (!Number.isFinite(d)) {
                  finish({ ok: false, seconds: 0 });
                  return;
               }
               finish({
                  ok: true,
                  seconds: Math.max(0, Math.round(d)),
                  rawSeconds: Math.max(0, d)
               });
            };
            video.onerror = () => {
               clearTimeout(timer);
               console.warn(LOG_TAG_UPLOAD_ITEM, "probeDurationFromBlobFile error");
               finish({ ok: false, seconds: 0 });
            };
            video.src = objectUrl;
         });
      },
      /**
       * 从本地视频截取一帧为 JPEG Blob（seek 后 canvas 导出），用于封面上传
       */
      captureVideoPosterJpegBlob(file) {
         return new Promise((resolve) => {
            if (!file || !(file instanceof Blob)) {
               resolve(null);
               return;
            }
            const objectUrl = URL.createObjectURL(file);
            const video = document.createElement("video");
            video.preload = "metadata";
            video.muted = true;
            video.playsInline = true;
            let settled = false;
            const finish = (blob) => {
               if (settled) {
                  return;
               }
               settled = true;
               clearTimeout(timer);
               URL.revokeObjectURL(objectUrl);
               video.removeAttribute("src");
               video.load();
               resolve(blob || null);
            };
            const timer = setTimeout(() => {
               console.warn(LOG_TAG_UPLOAD_ITEM, "captureVideoPosterJpegBlob timeout");
               finish(null);
            }, 25000);
            video.onseeked = () => {
               clearTimeout(timer);
               try {
                  const vw = video.videoWidth;
                  const vh = video.videoHeight;
                  if (!vw || !vh) {
                     finish(null);
                     return;
                  }
                  const maxSide = 1280;
                  let dw = vw;
                  let dh = vh;
                  if (dw > maxSide || dh > maxSide) {
                     const scale = maxSide / Math.max(dw, dh);
                     dw = Math.round(dw * scale);
                     dh = Math.round(dh * scale);
                  }
                  const canvas = document.createElement("canvas");
                  canvas.width = dw;
                  canvas.height = dh;
                  const ctx = canvas.getContext("2d");
                  ctx.drawImage(video, 0, 0, dw, dh);
                  canvas.toBlob(
                     (blob) => finish(blob || null),
                     "image/jpeg",
                     0.85
                  );
               } catch (err) {
                  console.warn(LOG_TAG_UPLOAD_ITEM, "captureVideoPosterJpegBlob canvas", err);
                  finish(null);
               }
            };
            video.onloadedmetadata = () => {
               const dur = video.duration;
               let seekTo = 0;
               if (Number.isFinite(dur) && dur > 0) {
                  seekTo = Math.min(0.15, Math.max(0.001, Math.min(dur * 0.25, dur - 0.01)));
               }
               try {
                  video.currentTime = seekTo;
               } catch (e) {
                  video.currentTime = 0;
               }
            };
            video.onerror = () => {
               clearTimeout(timer);
               console.warn(LOG_TAG_UPLOAD_ITEM, "captureVideoPosterJpegBlob video error");
               finish(null);
            };
            video.src = objectUrl;
         });
      },
      /** 封面走图片上传参数，与课程编辑 uploadUrl（fileType=1）一致 */
      uploadPosterImageBlob(blob) {
         return new Promise((resolve) => {
            if (!blob || !(blob instanceof Blob)) {
               resolve("");
               return;
            }
            const formData = new FormData();
            formData.append("uploadFile", blob, "video-cover.jpg");
            const uploadUrl =
               this.$util.domain +
               `/adminapi/v1/basicTools/v650/file/upload?uploadImageType=${this.uploadImageType}&fileType=1`;
            this.$http({
               method: "POST",
               url: uploadUrl,
               body: formData,
               headers: this.uploadHeaders
            })
               .then((res) => {
                  const body = res.body || res.data || {};
                  const code = body.error;
                  if (code !== 10000 && code !== 0) {
                     console.warn(LOG_TAG_UPLOAD_ITEM, "uploadPosterImageBlob api", body.message);
                     resolve("");
                     return;
                  }
                  const raw = body.data;
                  const url =
                     typeof raw === "string"
                        ? raw.trim()
                        : raw && typeof raw.url === "string"
                           ? raw.url.trim()
                           : "";
                  resolve(url || "");
               })
               .catch((err) => {
                  console.warn(LOG_TAG_UPLOAD_ITEM, "uploadPosterImageBlob catch", err);
                  resolve("");
               });
         });
      },
      // 上传成功钩子（v650 接口 data 为资源 URL 字符串，与课程编辑 uploadFile 一致）
      onSuccess(response, file) {
         const ok = response.error === 10000 || response.error === 0;
         if (!ok) {
            this.$emit("upload-state", false);
            this.$util.errorMsg("上传失败");
            return;
         }
         const raw = response.data;
         const serverUrl =
            typeof raw === "string"
               ? raw.trim()
               : raw && typeof raw.url === "string"
                  ? raw.url.trim()
                  : "";
         this.uploadUrl = serverUrl || (file && file.url) || "";
         this.fileName = file.name;

         const emitDone = (durationMeta, posterUrl) => {
            this.$emit("onUrls", this.uploadUrl, raw, durationMeta, posterUrl || "");
            this.$emit("upload-state", false);
         };

         if (String(this.fileType) === "3") {
            const blobFile =
               (file && file.raw) ||
               (file && file instanceof File ? file : null);
            if (blobFile) {
               Promise.all([
                  this.probeDurationFromBlobFile(blobFile),
                  this.captureVideoPosterJpegBlob(blobFile).then((jpegBlob) =>
                     jpegBlob ? this.uploadPosterImageBlob(jpegBlob) : Promise.resolve("")
                  )
               ])
                  .then(([durationMeta, posterUrl]) => emitDone(durationMeta, posterUrl || ""))
                  .catch(() => emitDone({ ok: false, seconds: 0 }, ""));
            } else {
               console.warn(LOG_TAG_UPLOAD_ITEM, "onSuccess video missing file.raw, fallback no local duration");
               emitDone({ ok: false, seconds: 0 }, "");
            }
            return;
         }

         emitDone(null, "");
      },
      // 上传失败钩子
      onError(response) {
         this.uploadUrl = "";
         this.$emit("upload-state", false);
         this.$emit("onUrls", this.uploadUrl, this.keys);
         if (response.status === 403) {
            this.SIGNOUT();
            location.href = "/#";
         }
         this.$util.errorMsg("上传失败");
      },
      // 删除图片钩子
      onRemove() {
         this.uploadUrl = "";
         this.$emit("upload-state", false);
         this.$emit("onUrls", this.uploadUrl, this.keys);
      },
   },
};
</script>

<style scoped>
.upload-wrapper .img-wrap {
   position: relative;
   width: 100%;
   height: 180px;
   line-height: 180px;
   border: 1px solid #c0ccda;
   border-radius: 4px;
   text-align: center;
}

.upload-wrapper .img-wrap img {
   position: absolute;
   left: 0;
   right: 0;
   top: 0;
   bottom: 0;
   width: 100%;
   height: 100%;
   object-fit: contain;
}

.upload-wrapper .img-wrap video {
   position: absolute;
   left: 0;
   right: 0;
   top: 0;
   bottom: 0;
   width: 100%;
   height: 100%;
   object-fit: contain;
}

.upload-wrapper .img-wrap .file-name {
   width: 100%;
   height: 100%;
   text-align: center;
   display: flex;
   justify-content: center;
   align-items: center;
}

.upload-wrapper .img-close {
   position: absolute;
   top: 0px;
   right: 0px;
   padding: 0;
}

.upload-wrapper .upload-tips {
   text-align: left !important;
   line-height: 1;
   margin: 0px;
}

.el-dragger__text {
   padding: 0px 20px;
}
</style>


// WEBPACK FOOTER //
// UploadItem.vue?60e4a755